import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from './theme/theme-provider';
import { AuthProvider } from './auth/auth-provider';
import { ProtectedRoute } from './auth/protected-route';
import { LoginPage } from './auth/login-page';
import { AppShell } from './components/layout/app-shell';
import { HomeRoute } from './features/home-route';
import { InvoiceRegisterPage } from './features/invoices/invoice-register-page';
import { InvoiceDetailPage } from './features/invoices/invoice-detail-page';
import { ProjectsPage } from './features/projects/projects-page';
import { ArchivedProjectsPage } from './features/projects/archived-projects-page';
import { ProjectDetailPage } from './features/projects/project-detail-page';
import { OrderPage } from './features/orders/order-page';
import { OrderHistoryPage } from './features/orders/order-history-page';
import { PayablesPage } from './features/payables/payables-page';
import { ContactsPage } from './features/contacts/contacts-page';
import { ApprovalsPage } from './features/approvals/approvals-page';
import { SettingsPage } from './features/settings/settings-page';
import './styles/base.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              <Route path="/logowanie" element={<LoginPage />} />

              <Route
                element={
                  <ProtectedRoute>
                    <AppShell />
                  </ProtectedRoute>
                }
              >
                <Route index element={<HomeRoute />} />
                <Route path="faktury" element={<InvoiceRegisterPage />} />
                <Route path="faktury/:id" element={<InvoiceDetailPage />} />

                {/* Every role has this; the API scopes it to the sites they run. */}
                <Route path="budowy" element={<ProjectsPage />} />
                <Route
                  path="budowy/archiwum"
                  element={
                    <ProtectedRoute allowedRoles={['ADMIN', 'ACCOUNTANT']}>
                      <ArchivedProjectsPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="budowy/:id" element={<ProjectDetailPage />} />

                {/* The history is open to every role — the API scopes it to
                    the manager's own orders and shows the office all of them,
                    which is also what the site card links into. */}
                <Route path="zamowienia" element={<OrderHistoryPage />} />

                {/* Placing one is not: the order is signed with the sender's
                    name and reply address, so it belongs to the person
                    standing on the site. */}
                <Route
                  path="zamowienia/nowe"
                  element={
                    <ProtectedRoute allowedRoles={['SITE_MANAGER']}>
                      <OrderPage />
                    </ProtectedRoute>
                  }
                />
                {/* Where the order form used to live. */}
                <Route path="zamowienie" element={<Navigate to="/zamowienia/nowe" replace />} />

                {/* Everybody's address book — the API enforces no roles here. */}
                <Route path="kontakty" element={<ContactsPage />} />

                <Route
                  path="rozrachunki"
                  element={
                    <ProtectedRoute allowedRoles={['ADMIN', 'ACCOUNTANT']}>
                      <PayablesPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="do-zatwierdzenia"
                  element={
                    <ProtectedRoute allowedRoles={['ADMIN', 'ACCOUNTANT']}>
                      <ApprovalsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="ustawienia"
                  element={
                    <ProtectedRoute allowedRoles={['ADMIN', 'ACCOUNTANT']}>
                      <SettingsPage />
                    </ProtectedRoute>
                  }
                />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
